import { useEffect, type RefObject } from 'react';

/**
 * Trap de foco Tab/Shift+Tab dentro do painel — primeiro↔último focável, sem
 * escapar para o resto do documento. Outros painéis com `role="dialog"` no kit
 * podem gerir foco pelo componente compartilhado que os implementa.
 * `enabled=false` desliga o trap inteiro — usado pelo shell no layout de push
 * (`modal={false}`), onde o resto da página segue interativo/tabável de
 * propósito. Não lida com Escape — cada consumidor mantém seu próprio
 * listener.
 */
export function useFocusTrap(panelRef: RefObject<HTMLElement | null>, enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      // Seletor alargado quando o explorer (takeover do chat) adotou o trap:
      // a faixa de busca tem input (combobox DE/PARA) e o painel de cortes tem
      // checkbox/range — um painel que começa ou termina num desses escaparia
      // do trap com o seletor button-shaped original. `:disabled` fora: um
      // Confirmar desabilitado como borda faria o wrap focar um nó infocável.
      const focusables = [
        ...panel.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]',
        ),
      ].filter(
        (el) =>
          el.tabIndex >= 0 &&
          !el.matches(':disabled') &&
          !el.closest('[hidden], [inert], [aria-hidden="true"]'),
      );
      const first = focusables[0];
      const last = focusables.at(-1);
      if (!first || !last) return;
      // O painel some de `focusables` (querySelectorAll pega descendentes, não
      // o próprio nó), mas ele É o activeElement logo após abrir (consumidores
      // focam o painel, não o primeiro item, pra o leitor de tela anunciar o
      // título antes de qualquer item). Sem este ramo, Shift+Tab a partir do
      // painel segue a ordem natural do documento e escapa do trap.
      if (document.activeElement === panel) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [panelRef, enabled]);
}
